/* ----------------------------------------------------------------------------
 * wakeRemainder -- F2/F7 (r2 owner device test, 10-10-2026 21:23 UTC).
 *
 * Evidence: "Hey Aura, push the next draft to booksesh.com" reached the
 * assistant as "To booksearch.com" (21:23:08) and, at 21:23:40, as "The next
 * draft to bookslash.com." -- the request capture (MediaRecorder, opened at the
 * local fire) loses the words spoken right after the wake phrase, while the
 * server's confirm clip (2.5 s pre-roll + 1 s after the fire) HEARD them
 * (transcript_len 42 for that check). The server now returns, on a CONFIRMED
 * check only, the words AFTER the wake phrase (`after_wake`; never the
 * pre-wake/bystander part). This module merges that remainder with the
 * capture transcript so the request contains everything after "Hey Aura".
 * PURE (no DOM/IO): unit-tested in __tests__/wake-remainder.test.ts.
 * -------------------------------------------------------------------------- */

const norm = (w: string): string => w.toLowerCase().replace(/[^a-z0-9]/g, '');

function words(s: string): string[] {
  return s.trim().split(/\s+/).filter((w) => w.length > 0);
}

/** Merge the confirm-clip remainder (words after the wake phrase, start of the
 *  request) with the request-capture transcript (which may have lost its first
 *  words). Overlap is found on normalised words: the longest suffix of the
 *  remainder that equals a prefix of the capture (allowing ONE garbled leading
 *  capture word). No overlap: if the capture already contains the whole
 *  remainder it wins; otherwise remainder + capture. */
export function mergeWakeRemainder(remainder: string | null | undefined, capture: string): string {
  const cap = (capture ?? '').trim();
  const rem = (remainder ?? '').trim();
  if (rem === '') return cap;
  if (cap === '') return rem;
  const a = words(rem);
  const b = words(cap);
  const an = a.map(norm);
  const bn = b.map(norm);
  // The capture already starts with the whole remainder -> nothing was lost.
  if (an.length <= bn.length && an.every((w, i) => w === bn[i])) return cap;
  let best = { k: 0, skip: 0 };
  for (let skip = 0; skip <= 1; skip++) {
    for (let k = Math.min(an.length, bn.length - skip); k >= 1; k--) {
      let match = true;
      for (let i = 0; i < k; i++) {
        if (an[an.length - k + i] !== bn[skip + i] || an[an.length - k + i] === '') { match = false; break; }
      }
      if (match) { if (k > best.k) best = { k, skip }; break; }
    }
  }
  // The whole remainder is already inside the capture -> nothing was lost.
  if (best.k === a.length) return cap;
  if (best.k > 0) {
    return [...a.slice(0, a.length - best.k), ...b.slice(best.skip)].join(' ');
  }
  // The remainder sits wholly inside the capture (capture began before it).
  const capN = ` ${bn.join(' ')} `;
  if (capN.includes(` ${an.join(' ')} `)) return cap;
  return `${rem} ${cap}`;
}

/** F7: a lone "Hey Aura" -- the confirm clip heard nothing after the wake
 *  phrase AND the open capture has no voiced span longer than the wake-word
 *  tail. `afterWake === null` means the server did not say (older server):
 *  NOT alone (keep the r2 chime-only behaviour). */
export function isLoneWake(afterWake: string | null | undefined, voicedSpanMs: number, tailMs: number): boolean {
  if (afterWake === null || afterWake === undefined) return false;
  if (words(afterWake).length > 0) return false;
  return voicedSpanMs < tailMs;
}

/* F9 (owner retest 10-10-2026 23:32:05 / 23:32:57): requests arrived as
 * "Help. Hey. What's happening today?" and "Hey. What's happening today?" -- the
 * tail of Aura's own "How can I help?" (raw mic, EC off) and wake-phrase
 * fragments leak into the capture. Strip LEADING standalone fragments only:
 * "help" / "how can I help" / "hey" / "hi" / "hello" / "aura" / "hey aura" /
 * "ok aura" that are punctuated as their own sentence or clause ("Help." "Hey,"),
 * plus an unpunctuated "hey aura" / "ok aura" lead-in. Never "Help me ...", never
 * a lone "okay"/"yes" (confirm answers), and never to an empty result. Pure. */
// conversational-actions (diagnosis F2, owner session 20:15:28 "Ora. What are the pending
// blogs?"): "Ora" is an STT variant of the wake word, stripped exactly like "Aura".
const ECHO_LEAD_RE = /^\s*(?:(?:how can i help(?: you)?|help|hey|hi|hello|aura|ora|(?:hey|hi|ok|okay) (?:aura|ora))[.!?,;:]+|(?:hey|hi|ok|okay) (?:aura|ora)\b[.!?,;:]*)\s+/i;

export function stripWakeEcho(transcript: string): string {
  let t = (transcript ?? '').trim();
  for (let i = 0; i < 4; i += 1) {
    const m = t.match(ECHO_LEAD_RE);
    if (!m) break;
    const rest = t.slice(m[0].length).trim();
    if (rest === '') break;
    t = rest;
  }
  return t;
}
